import type { HtmlAttributes } from '@studio/design-system/props';
import type { Settings } from '@studio/models/control';
import { ControlDefaults } from '@studio/models/control';
import { ViscaSerialDefaults } from '@studio/models/control/visca-serial';
import type { Configuration } from '@studio/models/control/visca-serial';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn, useMount } from '@studio/react-utils/hooks';
import type { T_Nullable } from '@tryiris-ai/js-types';
import { defaultsDeep } from 'lodash-es';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { shallow } from 'zustand/shallow';
import { useProducer, useStore } from '../store/hooks.js';
import { ViscaSerial as _ViscaSerial } from '#~/control/components/editor/index.js';
import { useAnalytics } from '#~/analytics/index.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/index.js';

/** @internal */
const Inner = (props: HtmlAttributes): ReactElement => {
  const producer = useProducer();
  const { track } = useAnalytics();

  const settings = useStore(
    ({ candidate }) =>
      // We know this is a normalized VISCA/Serial configuration.
      candidate.control as Configuration,
    shallow
  );

  // Per ENG-3960 §E12 — canonical device cohort props read outside producer callback.
  const deviceModelId = useStore((µ) => µ.manufacturer?.config?.model?.id?.toString());
  const deviceBrandId = useStore((µ) => µ.manufacturer?.config?.model?.brandId?.toString());

  const handleChange = useLatestFn((value: Settings) => {
    producer((draft) => {
      draft.candidate.control = value;
      track(EVENT_NAMES.EDIT_ADVANCED_CONTROL_SETTINGS, EVENT_CATEGORIES.ADVANCED_SETTINGS, {
        controlSettingKey: 'viscaSerial',
        controlSettingValue: value,
        ...(deviceModelId ? { device_model_id: deviceModelId } : {}),
        ...(deviceBrandId ? { device_brand_id: deviceBrandId } : {})
      });
    });
  });

  return <_ViscaSerial settings={toAccessor(settings, handleChange)} {...props} />;
};

/** Visca/Serial settings. */
export const ViscaSerial = (props: HtmlAttributes): T_Nullable<ReactElement> => {
  const [isPrimed, setPrimed] = useState(false);
  const producer = useProducer();

  // On mount, normalize the Visca/Serial control settings.
  useMount(() => {
    queueMicrotask(() => {
      producer(({ candidate }) => {
        const { control } = candidate;

        // Normalize configuration.
        candidate.control = defaultsDeep(
          { ...control },
          ViscaSerialDefaults,
          ControlDefaults
        ) as Settings<Configuration>;
      });

      setPrimed(true);
    });
  });

  return isPrimed ? <Inner {...props} /> : null;
};
